import React from 'react';

import { CoreIcon } from '@aspid/ui';
import { Box, Typography } from '@mui/material';
import Dropzone, {
  type DropEvent,
  type FileRejection,
  type FileWithPath,
} from 'react-dropzone';

import { IconCollection } from '@components/icons/iconCollection';
import { spreadSxProperty } from '@utils/helpers';
import { useNumbro } from '@utils/hooks';

import { alert } from '../../Alert';
import {
  acceptPropertyAsAcceptAttribute,
  convertBytesToMbsOrKbs,
} from '../DropZone.helpers';

import { styles } from './DropZoneAreaBase.styles';

import type { DropZoneBaseProperties } from './DropZoneAreaBase.types';

const defaultFileLimitExceedMessage = (filesLimit: number) =>
  `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`;
const defaultGetDropRejectMessage = (
  rejectedFile: FileRejection,
  acceptFiles: string,
  maxFileSize: number,
) => {
  let message = `File ${rejectedFile.file.name} was rejected. `;
  if (!acceptFiles.includes(rejectedFile.file.type)) {
    message += 'File type not supported. ';
  }
  if (rejectedFile.file.size > maxFileSize) {
    message += `File is too big. Size limit is ${convertBytesToMbsOrKbs(
      maxFileSize,
    )}.`;
  }
  return message;
};

export const DropZoneAreaBase: React.FC<DropZoneBaseProperties> = ({
  dataTest = 'dropzone',
  accept = {},
  filesLimit = 1,
  icon,
  fileObjects = [],
  maxFileSize = 30_000_000,
  dropzoneText = 'Drag and drop files here or click to select files',
  getFileLimitExceedMessage = defaultFileLimitExceedMessage,
  getDropRejectMessage = defaultGetDropRejectMessage,
  onAdd,
  onDrop,
  onDropRejected,
  inputProps,
  dropzoneProps,
  dropZoneStyles = [],
  dropzoneParagraphStyles = [],
  filesCount = 0,
  disabled,
}) => {
  const isMultiple = filesLimit > 1;
  const acceptFiles = acceptPropertyAsAcceptAttribute(accept);
  const numbro = useNumbro();

  const handleDropAccepted = async <T extends File>(
    acceptedFiles: T[],
    event: DropEvent,
  ) => {
    if (filesLimit > 1 && filesCount + acceptedFiles.length > filesLimit) {
      alert.error(getFileLimitExceedMessage(filesLimit));
      return;
    }

    /**
     * react-dropzone types this callback as `<T extends File>`, but at runtime it
     * always yields FileWithPath: files are resolved through file-selector, which
     * attaches `path` and `relativePath`. Consumers rely on `path` (DropZone.tsx),
     * so narrow here rather than weakening the public prop types to File[].
     */
    const droppedFiles = acceptedFiles as unknown as FileWithPath[];

    // Notify Drop event
    onDrop?.(droppedFiles, event);

    // Notify added files
    onAdd?.(droppedFiles);
  };

  const handleDropRejected = (
    rejectedFiles: FileRejection[],
    event: DropEvent,
  ) => {
    let message = '';
    if (fileObjects.length + rejectedFiles.length > filesLimit) {
      message = getFileLimitExceedMessage(filesLimit);
    } else {
      for (const rejectedFile of rejectedFiles) {
        message = getDropRejectMessage(rejectedFile, acceptFiles, maxFileSize);
      }
    }

    onDropRejected?.(rejectedFiles, event);
    alert.error(message);
  };

  return (
    <Dropzone
      {...dropzoneProps}
      accept={accept}
      onDropAccepted={handleDropAccepted}
      onDropRejected={handleDropRejected}
      maxSize={maxFileSize}
      multiple={isMultiple}
      maxFiles={filesLimit}
      disabled={disabled}
    >
      {({ getRootProps, getInputProps, isDragActive, isDragAccept }) => {
        return (
          <>
            <Box
              data-testid={dataTest}
              component='div'
              {...getRootProps()}
              sx={[
                theme => ({
                  ...styles(theme).root,
                  ...(isDragAccept && styles(theme).active),
                  ...(isDragActive && !isDragAccept && styles(theme).invalid),
                  ...(disabled && styles(theme).disabled),
                }),
                ...spreadSxProperty(dropZoneStyles),
              ]}
            >
              <input
                data-testid={`${dataTest}_input`}
                {...getInputProps(inputProps)}
              />
              <Box
                data-testid={`${dataTest}_content`}
                sx={theme => styles(theme).textContainer}
              >
                <Typography
                  data-testid={`${dataTest}_text`}
                  variant='h5'
                  component='p'
                  sx={[
                    theme => ({
                      ...styles(theme).text,
                      px: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                      textAlign: 'center',
                      ...(disabled && { color: 'text.disabled' }),
                    }),
                    ...spreadSxProperty(dropzoneParagraphStyles),
                  ]}
                >
                  {dropzoneText}
                </Typography>
                <CoreIcon
                  dataTest={`${dataTest}_icon`}
                  icon={icon || IconCollection.uploadCloud}
                  sx={{
                    fontSize: '3.5rem',
                    mb: 2,
                    color: disabled ? 'text.disabled' : 'primary.main',
                  }}
                />
              </Box>
            </Box>
            <Typography
              data-testid={`${dataTest}_size_hint`}
              variant='small'
              color='textSecondary'
              sx={{ m: { xs: 0, sm: 0 }, mt: { xs: 0, sm: 1 } }}
            >{`Размер файла до ${numbro(maxFileSize).format({
              mantissa: 2,
              average: true,
              output: 'byte',
              base: 'binary',
            })}`}</Typography>
          </>
        );
      }}
    </Dropzone>
  );
};
